import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Calendar, ArrowLeft, Sparkles, Send, Phone } from 'lucide-react'
import SEO from '../components/SEO'

export default function BlogPostDetail({ defaultSlug }) {
  const { slug: paramSlug } = useParams()
  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const activeSlug = (defaultSlug || paramSlug || (typeof window !== 'undefined' ? window.location.pathname.replace(/^\/(blog\/)?/, '').replace(/\/$/, '') : '')).toLowerCase()

  useEffect(() => {
    setLoading(true)
    setError(false)

    // Hent alle blogindlæg for at sikre, at alle slug- og URL-varianter matcher direkte
    fetch('/_api/public/content/blog_posts')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente blogindlæg')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const norm = activeSlug.toLowerCase()
          let matched = data.find((p) => {
            const pSlug = (p.post_slug?.da || p.post_slug || p.slug?.da || p.slug || '').toLowerCase()
            return pSlug === norm
          })

          if (!matched) {
            if (norm.includes('koster')) {
              matched = data.find((p) => (p.post_slug?.da || p.slug?.da || '').includes('koster'))
            } else if (norm.includes('bryllup') && (norm.includes('vaelg') || norm.includes('koebenhavn'))) {
              matched = data.find((p) => (p.post_slug?.da || p.slug?.da || '').includes('bryllup-saadan-vaelger-i'))
            } else if (norm.includes('vinyl')) {
              matched = data.find((p) => (p.post_slug?.da || p.slug?.da || '').includes('vinyl'))
            } else if (norm.includes('firmafest')) {
              matched = data.find((p) => (p.post_slug?.da || p.slug?.da || '').includes('firmafest'))
            } else if (norm.includes('foedselsdag') || norm.includes('fødselsdag')) {
              matched = data.find((p) => (p.post_slug?.da || p.slug?.da || '').includes('foedselsdag'))
            } else if (norm.includes('vaelg') || norm.includes('rigtige') || norm.includes('spotify')) {
              matched = data.find((p) => (p.post_slug?.da || p.slug?.da || '').includes('vaelger') || (p.title?.da || '').includes('rigtige DJ'))
            }
          }

          if (matched) {
            setPost(matched)
            setLoading(false)
            return
          }
        }

        // Fallback til by-slug hvis ikke fundet i listen
        return fetch(`/_api/public/by-slug/da/${activeSlug}`)
          .then((res) => {
            if (!res.ok) throw new Error('Blogindlæg ikke fundet')
            return res.json()
          })
          .then((singleData) => {
            if (singleData) {
              setPost(singleData)
            } else {
              setError(true)
            }
            setLoading(false)
          })
      })
      .catch((err) => {
        console.error('Fejl ved hentning af blogindlæg:', err)
        setError(true)
        setLoading(false)
      })
  }, [activeSlug])

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-black text-white">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-amber-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-zinc-400">Henter blogindlæg...</p>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-black text-white px-4">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-amber-400">Blogindlægget blev ikke fundet</h2>
          <p className="mt-2 text-zinc-400">Siden du leder efter findes desværre ikke eller er blevet flyttet.</p>
          <Link
            to="/blog"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#bb9b56] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#a88645]"
          >
            <ArrowLeft className="h-4 w-4" />
            Tilbage til bloggen
          </Link>
        </div>
      </div>
    )
  }

  const title = post.title?.da || post.title
  const summary = post.summary?.da || post.summary
  const content = post.content?.da || post.content
  const image = post.image?.[0]
  const date = post.date
  const optimizedHeroImage = image?.startsWith('https://assets.auxodev.dk/')
    ? `https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/${image.replace('https://assets.auxodev.dk/', '')}`
    : image

  // Bestem den kanoniske URL for det specifikke blogindlæg
  let canonicalPath = `/${activeSlug}/`
  if (activeSlug.includes('koster')) canonicalPath = '/hvad-koster-en-dj-til-bryllup/'
  else if (activeSlug.includes('bryllup') && (activeSlug.includes('vaelg') || activeSlug.includes('koebenhavn'))) canonicalPath = '/dj-til-bryllup-i-koebenhavn-saadan-vaelger-i/'
  else if (activeSlug.includes('vinyl')) canonicalPath = '/vinyl-dj-til-fest/'
  else if (activeSlug.includes('firmafest')) canonicalPath = '/dj-til-firmafest-saadan-vaelger-du-rigtigt/'
  else if (activeSlug.includes('foedselsdag') || activeSlug.includes('fødselsdag')) canonicalPath = '/dj-til-foedselsdag-der-fylder-dansegulvet/'
  else if (activeSlug.includes('vaelg') || activeSlug.includes('rigtige') || activeSlug.includes('spotify')) canonicalPath = '/vaelg-den-rigtige-dj-til-din-fest/'

  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `https://www.djron9.dk${canonicalPath}#article`,
        "headline": title,
        "description": summary,
        "image": image ? [image] : [],
        "datePublished": date,
        "author": {
          "@type": "Person",
          "name": post.author?.da || post.author || "Ronni Berlau"
        },
        "publisher": {
          "@type": "EntertainmentBusiness",
          "name": "DJ Ron9",
          "url": "https://www.djron9.dk/"
        },
        "mainEntityOfPage": `https://www.djron9.dk${canonicalPath}`
      },
      {
        "@type": "BreadcrumbList",
        "@id": `https://www.djron9.dk${canonicalPath}#breadcrumb`,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://www.djron9.dk/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://www.djron9.dk/blog/"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": title,
            "item": `https://www.djron9.dk${canonicalPath}`
          }
        ]
      }
    ]
  }

  return (
    <div className="bg-black text-white min-h-screen pb-20">
      <SEO
        title={`${title} | DJ Ron9 Blog`}
        description={summary ? (summary.length > 155 ? summary.slice(0, 155) + '...' : summary) : 'Læs det nyeste blogindlæg fra DJ Ron9.'}
        canonical={canonicalPath}
        ogImage={image}
        ogType="article"
        schema={articleSchema}
      />
      {/* Header Section */}
      <section className="relative flex min-h-[35vh] sm:min-h-[50vh] items-center overflow-hidden py-12 sm:py-20">
        <div className="absolute inset-0 z-0">
          <img
            src={optimizedHeroImage || image}
            alt={title}
            width="1600"
            height="500"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover opacity-60 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-300 hover:text-amber-400 mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Tilbage til bloggen
          </Link>

          <div className="flex items-center gap-4 text-xs text-zinc-400 mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-white/70" />
              <span>{date}</span>
            </div>
            {post.author && (
              <span className="text-zinc-300">
                Af <span className="text-white font-medium">{post.author?.da || post.author}</span>
              </span>
            )}
          </div>

          <h1 className="font-serif text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl text-white leading-tight">
            {title}
          </h1>

          <p className="mt-6 text-base text-zinc-200 leading-relaxed italic border-l-2 border-amber-500/50 pl-4 max-w-3xl">
            {summary}
          </p>

          {/* Contact CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#bb9b56] px-8 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-[#a88645] transition-all"
            >
              <Send className="h-4 w-4" />
              Få et uforpligtende tilbud
            </Link>
            <div
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-full border border-zinc-700 bg-zinc-900/80 px-8 py-3.5 text-sm font-bold text-white"
            >
              <Phone className="h-4 w-4 text-[#bb9b56]" />
              50 99 82 82
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Article Content */}
          <article className="rounded-2xl border border-zinc-900 bg-zinc-950/50 p-6 md:p-10 shadow-backlight-hover md:shadow-backlight md:hover:shadow-backlight-hover transition-all duration-300">
            <div
              className="prose prose-invert max-w-none text-zinc-300 leading-relaxed space-y-6
                [&_h1]:font-extrabold [&_h1]:text-2xl [&_h1]:md:text-3xl [&_h1]:text-white [&_h1]:mt-8 [&_h1]:mb-4
                [&_h2]:font-extrabold [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:text-white [&_h2]:mt-8 [&_h2]:mb-3
                [&_h3]:font-bold [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:text-white [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:leading-snug
                [&_h4]:font-bold [&_h4]:text-base [&_h4]:md:text-lg [&_h4]:text-white [&_h4]:mt-4 [&_h4]:mb-2
                prose-headings:font-serif prose-headings:text-white prose-headings:font-bold
                prose-p:text-sm prose-p:md:text-base prose-p:text-zinc-300 prose-p:leading-relaxed
                prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-4
                prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-4
                prose-li:text-zinc-300 prose-li:text-sm prose-li:md:text-base
                prose-strong:text-white prose-strong:font-bold"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </article>

          {/* Bottom CTA Card */}
          <div className="mt-16 rounded-2xl border border-amber-500/20 bg-gradient-to-r from-amber-500/5 to-yellow-500/5 p-8 text-center shadow-backlight-hover md:shadow-backlight md:hover:shadow-backlight-hover transition-all duration-300">
            <h3 className="font-serif text-2xl font-bold text-white">Skal jeg sikre den perfekte musik til din fest?</h3>
            <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">
              Uanset om du planlægger bryllup, firmafest eller fødselsdag, står jeg klar til at skabe den ultimative festoplevelse.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/#kontakt"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#bb9b56] px-8 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-[#a88645]"
              >
                <Send className="h-4 w-4" />
                Få et uforpligtende tilbud
              </a>
              <div
                className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-full border border-zinc-800 bg-zinc-950 px-8 py-3.5 text-sm font-bold text-white"
              >
                <Phone className="h-4 w-4 text-[#bb9b56]" />
                50 99 82 82
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
